import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Copy, Trash2, Loader2, KeyRound, Ticket, Activity, Music4, LogOut,
} from "lucide-react";
import { api, errMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { startOwnerLogin } from "../components/StudioHeader";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-[#1E2C48] bg-[#0D1424] p-4">
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="w-4 h-4 text-amber-300" />
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-2 font-heading text-2xl font-extrabold text-slate-50">{value}</p>
    </div>
  );
}

export default function OwnerDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({ code: "", allowance: "25", unlimited: false, expiresAt: "", note: "" });

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get("/codes"), api.get("/codes/stats")]);
      setCodes(c.data); setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) navigate("/");
      else toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    if (!user.is_owner) { navigate("/"); return; }
    load();
  }, [authLoading, user, load, navigate]);

  const createCode = async () => {
    setCreating(true);
    try {
      const body = {
        code: form.code.trim() || null,
        allowance: form.unlimited ? null : parseInt(form.allowance, 10) || 0,
        expiresAt: form.expiresAt ? new Date(form.expiresAt + "T23:59:59").toISOString() : null,
        note: form.note.trim(),
      };
      await api.post("/codes", body);
      setForm({ code: "", allowance: "25", unlimited: false, expiresAt: "", note: "" });
      toast.success("Access code created");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/codes/${c.id}`, { active: !c.active });
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/codes/${c.id}`);
      toast.success("Code deleted");
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (e) {
      console.warn("Clipboard write failed", e?.message);
    }
    toast.success("Code copied");
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070B14]">
        <Loader2 className="w-6 h-6 animate-spin text-amber-300" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#070B14] text-slate-200 px-6 text-center">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E6C66E] to-[#D4AF37] flex items-center justify-center">
          <KeyRound className="w-6 h-6 text-[#0B0E14]" />
        </div>
        <h1 className="mt-5 font-heading text-2xl font-extrabold text-slate-50">Owner sign in</h1>
        <p className="mt-2 text-sm text-slate-400 max-w-sm">
          Sign in with the owner Google account to manage buyer access codes.
        </p>
        <Button
          data-testid="owner-google-login-button"
          onClick={startOwnerLogin}
          className="mt-6 h-12 px-6 bg-gradient-to-r from-[#E6C66E] to-[#D4AF37] text-[#0B0E14] font-bold"
        >
          Continue with Google
        </Button>
        <button onClick={() => navigate("/")} className="mt-4 text-sm text-slate-400 hover:text-amber-200">
          Back to generator
        </button>
      </div>
    );
  }

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "Never");

  return (
    <div className="min-h-screen bg-[#070B14] text-slate-200">
      <header className="sticky top-0 z-30 border-b border-[#1E2C48] bg-[#070B14]/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#E6C66E] to-[#D4AF37] flex items-center justify-center">
              <Music4 className="w-4 h-4 text-[#0B0E14]" strokeWidth={2.5} />
            </div>
            <h1 className="font-heading text-base sm:text-lg font-extrabold text-slate-50">Owner Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="back-to-generator"
              onClick={() => navigate("/")}
              size="sm"
              className="bg-[#121B30] border border-[#1E2C48] text-slate-100 hover:bg-[#1a2540] h-9"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Generator</span>
            </Button>
            <Button
              data-testid="owner-logout"
              onClick={async () => { await logout(); navigate("/"); }}
              size="sm" variant="ghost"
              className="text-slate-400 hover:text-amber-200 h-9 px-2"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={Ticket} label="Total codes" value={stats?.total_codes ?? 0} />
          <StatCard icon={KeyRound} label="Active codes" value={stats?.active_codes ?? 0} />
          <StatCard icon={Music4} label="Songs generated" value={stats?.total_generations ?? 0} />
          <StatCard icon={Activity} label="Code generations" value={stats?.total_code_generations ?? 0} />
        </div>

        {/* Create code */}
        <div className="rounded-2xl border border-[#1E2C48] bg-[#0D1424]/60 p-5 space-y-4">
          <h2 className="font-heading text-lg font-bold text-slate-50">Create buyer access code</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-amber-400/90">Code (optional)</Label>
              <Input
                data-testid="new-code-input"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="Auto-generate"
                className="h-11 bg-[#121B30] border-[#1E2C48] text-slate-100 uppercase"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-amber-400/90">Generation allowance</Label>
              <Input
                data-testid="new-code-allowance"
                type="number" min="1"
                value={form.allowance}
                disabled={form.unlimited}
                onChange={(e) => setForm({ ...form, allowance: e.target.value })}
                className="h-11 bg-[#121B30] border-[#1E2C48] text-slate-100 disabled:opacity-50"
              />
              <label className="flex items-center gap-2 pt-1 text-xs text-slate-400">
                <Switch
                  data-testid="new-code-unlimited"
                  checked={form.unlimited}
                  onCheckedChange={(v) => setForm({ ...form, unlimited: v })}
                />
                Unlimited
              </label>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-amber-400/90">Expiration (optional)</Label>
              <Input
                data-testid="new-code-expires"
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                className="h-11 bg-[#121B30] border-[#1E2C48] text-slate-100"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-amber-400/90">Buyer note (optional)</Label>
              <Input
                data-testid="new-code-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="e.g. Etsy order #1234"
                className="h-11 bg-[#121B30] border-[#1E2C48] text-slate-100"
              />
            </div>
          </div>
          <Button
            data-testid="owner-create-code-button"
            onClick={createCode}
            disabled={creating}
            className="h-11 px-5 bg-gradient-to-r from-[#E6C66E] to-[#D4AF37] text-[#0B0E14] font-bold"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Create code</>}
          </Button>
        </div>

        {/* Codes table */}
        <div className="rounded-2xl border border-[#1E2C48] bg-[#0D1424]/60 overflow-hidden">
          <div className="px-5 py-3 border-b border-[#1E2C48]">
            <h2 className="font-heading text-lg font-bold text-slate-50">Access codes</h2>
          </div>
          {codes.length === 0 ? (
            <p className="p-8 text-center text-slate-400 text-sm">No codes yet. Create one above to share with a buyer.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="codes-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-[#1E2C48]">
                    <th className="px-5 py-3">Code</th>
                    <th className="px-4 py-3">Note</th>
                    <th className="px-4 py-3">Used / Allowance</th>
                    <th className="px-4 py-3">Expires</th>
                    <th className="px-4 py-3">Active</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => (
                    <tr key={c.id} data-testid={`code-row-${c.code}`} className="border-b border-[#1E2C48]/60 hover:bg-[#121B30]/40">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-amber-200">{c.code}</span>
                          <button onClick={() => copyCode(c.code)} className="text-slate-500 hover:text-amber-200" data-testid={`copy-code-${c.code}`}>
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-300 max-w-[180px] truncate">{c.note || "—"}</td>
                      <td className="px-4 py-3 text-slate-300">
                        {c.used} / {c.allowance === null ? "∞" : c.allowance}
                      </td>
                      <td className="px-4 py-3 text-slate-300">{fmtDate(c.expiresAt)}</td>
                      <td className="px-4 py-3">
                        <Switch
                          data-testid={`toggle-code-${c.code}`}
                          checked={c.active}
                          onCheckedChange={() => toggleActive(c)}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`delete-code-${c.code}`}
                          onClick={() => remove(c)}
                          className="text-slate-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
